import React from "react";
import { useSelector } from "react-redux";
import { selectAllEntries } from "../store/entries-slice";
import dayjs from "dayjs";
import customParseFormat from 'dayjs/plugin/customParseFormat';
dayjs.extend(customParseFormat);
import { I18N } from 'aurelia-i18n';

export default function Overview() {
    const i18n = new I18N();
    const entries = useSelector(selectAllEntries);
    const daysWithEntries = Array.from(new Set(entries.filter(e => e.day).map(e => e.day))).sort((a, b) => parseInt(a) - parseInt(b));
    const sortedDays = daysWithEntries.length ? daysWithEntries : [1];
    const entriesWithoutDay = entries.filter(e => !e.day);

    const grouped = sortedDays.reduce((acc, day) => {
        acc[day] = [
            ...entries.filter(e => String(e.day) === String(day)),
            ...entriesWithoutDay
        ];
        return acc;
    }, {});

    const formatTime = (time) => {
        if (!time) return null;
        let cleanedTime = String(time).replace(/\./g, ':');
        const parsed = dayjs(cleanedTime, ["HH:mm", "HHmm", "H:mm"], true);
        if (!parsed.isValid()) {
            return time;
        }
        return parsed.format("HH:mm");
    };

    const isAllDays = (entry) => {
        const totalDays = sortedDays.length;
        return entry.duration === totalDays || entry.duration === String(totalDays);
    };

    const handlePrint = () => {
        window.print();
    };

    React.useEffect(() => {
        const style = document.createElement('style');
        style.innerHTML = `
            @media print {
                body * { visibility: hidden !important; }
                .print-overview-root, .print-overview-root * { visibility: visible !important; }
                .print-overview-root { 
                    position: fixed !important; 
                    left: 0; 
                    top: 0; 
                    width: 100vw !important; 
                    background: none !important; 
                    box-shadow: none !important; 
                    padding: 0 !important; 
                    margin: 0 !important; 
                    z-index: 9999 !important;
                }
                .wrapper.overview {
                    padding: 4px !important;
                    margin: 0 !important;
                    background: none !important;
                }
                .print-btn { display: none !important; }
                .wrapper.overview > div {
                    margin-bottom: 0.5em !important;
                    padding: 8px !important;
                    box-shadow: none !important;
                }
                ul {
                    margin: 0 !important;
                    padding: 0 !important;
                }
                li {
                    margin-bottom: 6px !important;
                    padding: 4px !important;
                    font-size: 13px !important;
                    background: none !important;
                    box-shadow: none !important;
                }
                h2, span, li {
                    font-size: 13px !important;
                }
                hr.print-hr {
                    border: none;
                    border-top: 1px solid #ccc;
                    margin: 8px 0 8px 0;
                    height: 0;
                    background: none;
                    display: block;
                }
            }
        `;
        document.head.appendChild(style);
        return () => { document.head.removeChild(style); };
    }, []);

    return (
        <div className="print-overview-root">
            <div className="wrapper overview" style={{ fontFamily: 'Source Sans 3, Arial, sans-serif', background: '#f7f9fa', borderRadius: 12, padding: 24 }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 32 }}>
                    <h2 style={{ color: '#22223b', fontWeight: 800, letterSpacing: 1, fontSize: 18, margin: 0 }}>{i18n.tr('sio.overview')}</h2>
                    <button 
                        onClick={handlePrint}
                        className="print-btn"
                        style={{
                            background: '#3A86FF',
                            color: 'white',
                            border: 'none',
                            padding: '8px 16px',
                            borderRadius: '6px',
                            cursor: 'pointer',
                            fontWeight: 600,
                            fontSize: 14,
                            display: 'flex',
                            alignItems: 'center',
                            gap: '8px'
                        }}
                    >
                        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                            <path d="M6 9V2h12v7M6 18H4a2 2 0 0 1-2-2v-5a2 2 0 0 1 2-2h16a2 2 0 0 1 2 2v5a2 2 0 0 1-2 2h-2"/>
                            <path d="M6 14h12v8H6z"/>
                        </svg>
                    </button>
                </div>
                {sortedDays.map((day, idx) => (
                    <React.Fragment key={day}>
                        <div style={{ marginBottom: '2.5em', background: '#fff', borderRadius: 10, boxShadow: '0 2px 8px #e0e0e0', padding: 24 }}>
                            <div style={{ display: 'flex', alignItems: 'center', marginBottom: 16 }}>
                                <span style={{ fontSize: 15, fontWeight: 700, color: '#3A86FF', marginRight: 16 }}>{i18n.tr('sio.field.day')} {day}</span>
                                <span style={{ height: 2, background: '#3A86FF', flex: 1, borderRadius: 2 }}></span>
                            </div>
                            <ul style={{ listStyle: 'none', padding: 0, margin: 0 }}>
                                {grouped[day] && grouped[day].length > 0 ? (
                                    grouped[day].map(entry => {
                                        const label = entry.service?.objectLabel || entry.service?.label || "[Unbenannt]";
                                        const start = formatTime(entry.time);
                                        const end = formatTime(entry.endTime);
                                        const timeDisplayString = (start && end) ? `${start} - ${end}` : (start || end);
                                        return (
                                            <li key={entry.identifier} style={{ display: 'flex', alignItems: 'center', marginBottom: 18, padding: 12, borderRadius: 8, background: '#f1f3f8', boxShadow: '0 1px 4px #ececec' }}>
                                                <span style={{ fontWeight: 600, fontSize: 13, color: '#22223b', flex: 1 }}>{label}</span>
                                                {isAllDays(entry) && (
                                                    <span
                                                        style={{
                                                            marginRight: 16,
                                                            display: 'flex',
                                                            alignItems: 'center',
                                                            gap: '4px',
                                                            background: 'rgba(255, 68, 68, 0.12)',
                                                            borderRadius: '16px',
                                                            padding: '2px 10px 2px 7px',
                                                            fontWeight: 600,
                                                            fontSize: 12,
                                                            color: '#ff4444',
                                                            lineHeight: 1.2,
                                                        }}
                                                        title="All days"
                                                        aria-label="All days"
                                                    >
                                                        <svg width="16" height="16" viewBox="0 0 24 24" fill="#ff4444" stroke="#ff4444" strokeWidth="2" style={{marginRight: 3}}>
                                                            <path d="M6 3v18"/>
                                                            <path d="M6 3l12 4-12 4"/>
                                                        </svg>
                                                        {i18n.tr('tourism-journey-calculation.all-days')}
                                                    </span>
                                                )}
                                                {timeDisplayString && (
                                                    <span style={{ marginLeft: 16, color: '#3A86FF', fontWeight: 500, fontSize: 11, letterSpacing: 1 }}>
                                                        {timeDisplayString}
                                                    </span>
                                                )}
                                            </li>
                                        );
                                    })
                                ) : (
                                    <li style={{ color: '#aaa', fontStyle: 'italic', padding: 12 }}>{i18n.tr('tourism-journey-calculation.daily-no-data')}</li>
                                )}
                            </ul>
                        </div>
                        {idx !== sortedDays.length - 1 && <hr className="print-hr" />}
                    </React.Fragment>
                ))}
            </div>
        </div>
    );
}
